import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MoveDown } from "lucide-react";
import { EVENTS, STRENGTHS, STEPS, COCKTAILS, IMG } from "../data/site";
import useSEO from "../hooks/useSEO";
import { MaskLines, Reveal } from "../components/anim";
import Marquee from "../components/Marquee";
import ParallaxImage from "../components/ParallaxImage";
import { Eyebrow, CTAButton } from "../components/ui-bits";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      className="relative h-[100svh] min-h-[640px] overflow-hidden"
    >
      <motion.video
        poster={IMG.hero}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        data-testid="hero-video"
        ref={(el) => {
          if (!el) return;
          el.muted = true;
          const tryPlay = () => el.play().catch(() => {});
          el.addEventListener("loadeddata", tryPlay);
          tryPlay();
        }}
        aria-label="Il banco Sprizziamoci in azione: bevande alla spina in self service"
        style={{ y }}
        initial={{ scale: 1.18 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 h-full w-full object-cover will-change-transform"
      >
        <source src="/hero.mp4" type="video/mp4" />
      </motion.video>
      <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/55 to-brand-ink/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/70 via-transparent to-transparent" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto max-w-7xl px-5 md:px-8 h-full flex flex-col justify-end pb-16 md:pb-24"
      >
        <p className="eyebrow">L'aperitivo on the road · Sprizziamoci</p>
        <MaskLines
          lines={["LA FESTA HA", "UN NUOVO MODO", "DI BRINDARE."]}
          as="h1"
          when="load"
          delay={0.25}
          stagger={0.13}
          lineClass="font-display font-semibold tracking-tight leading-[0.95] text-[clamp(3rem,9vw,7.2rem)]"
          className="mt-5"
        />
        <div className="mt-8 flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-16">
          <Reveal delay={0.75}>
            <p className="max-w-md text-sm md:text-base leading-relaxed text-white/75">
              Banco self service con erogatori alla spina direttamente al tuo evento: i tuoi
              ospiti si servono da soli, si divertono e brindano senza attese.
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <CTAButton to="/contatti" testId="hero-cta-evento">
                Organizza il tuo evento
              </CTAButton>
              <CTAButton to="/concept" variant="outline" testId="hero-cta-scopri">
                Scopri Sprizziamoci
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/40"
      >
        <MoveDown size={15} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

function FormatInnovativo() {
  return (
    <section data-testid="format-innovativo" className="py-24 md:py-32 bg-brand-coal border-y border-white/[0.06]">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-16 lg:grid-cols-12 items-center">
        <div className="lg:col-span-7">
          <Eyebrow>Il format</Eyebrow>
          <MaskLines
            lines={["UN FORMAT INNOVATIVO.", "IL DRINK SI VERSA DA SOLO."]}
            when="view"
            lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.2rem,4.6vw,4.2rem)]"
            className="mt-6"
          />
          <Reveal delay={0.15}>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-white/65 max-w-xl">
              La nostra forza è l'installazione di banchi attrezzati con erogatori alla spina: un
              sistema organizzato in cui il cliente è libero di servirsi da solo. Niente file,
              niente camerieri: l'aperitivo diventa un'esperienza.
            </p>
          </Reveal>
          <div className="mt-10 space-y-0 border-t border-white/[0.07]">
            {STRENGTHS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="flex gap-6 border-b border-white/[0.07] py-5 group">
                  <span className="font-display italic text-brand-orange text-xl leading-none pt-0.5">
                    {s.n}
                  </span>
                  <div>
                    <p className="font-display text-xl md:text-2xl font-semibold group-hover:text-brand-orange transition-colors duration-300">
                      {s.title}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <CTAButton to="/concept" testId="concept-teaser-cta">
              Scopri il concept self service
            </CTAButton>
          </Reveal>
        </div>
        <div className="lg:col-span-5 relative">
          <Reveal>
            <ParallaxImage
              src={IMG.bancoErogatori}
              alt="Il banco self service Sprizziamoci con i quattro erogatori alla spina"
              speed={0.14}
              className="relative rounded-3xl h-[420px] md:h-[560px]"
            />
          </Reveal>
          <Reveal delay={0.25}>
            <div className="relative lg:absolute lg:-bottom-8 lg:-left-8 lg:max-w-xs rounded-2xl border border-brand-orange/30 bg-brand-ink/90 backdrop-blur-xl p-6 mt-6 lg:mt-0">
              <p className="eyebrow mb-3">La nostra forza</p>
              <p className="text-sm leading-relaxed text-white/75">
                Banchi installati, sistema organizzato, ospiti liberi di servirsi da soli: nessuna
                attesa, solo brindisi.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function EventCard({ e, className }) {
  return (
    <Link
      to={e.path}
      data-testid={`event-card-${e.slug}`}
      className={`group relative block overflow-hidden rounded-3xl border border-white/[0.06] ${className}`}
    >
      <img
        src={e.image}
        alt={e.cardTitle}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/25 to-transparent" />
      <span
        data-testid={`event-card-badge-${e.slug}`}
        className={`absolute top-5 left-5 z-10 inline-flex rounded-full border px-3.5 py-1.5 text-[9px] font-semibold uppercase tracking-[0.22em] backdrop-blur ${
          e.slug === "eventi-pubblici"
            ? "border-brand-orange/50 bg-brand-ink/60 text-brand-orange"
            : "border-white/25 bg-brand-ink/40 text-white/85"
        }`}
      >
        {e.badge}
      </span>
      <div className="relative h-full flex flex-col justify-end p-7 md:p-9">
        <span className="mb-4 inline-flex w-fit rounded-full border border-white/25 bg-brand-ink/40 backdrop-blur px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/85">
          {e.label}
        </span>
        <h3 className="font-display font-semibold leading-[1.05] text-2xl md:text-4xl max-w-md">
          {e.cardTitle}
        </h3>
        <span className="mt-6 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-orange">
          {e.cta}
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-orange/50 transition-all duration-300 group-hover:bg-brand-orange group-hover:text-brand-ink">
            <ArrowUpRight size={14} />
          </span>
        </span>
      </div>
    </Link>
  );
}

function EventsBento() {
  return (
    <section id="eventi" className="py-24 md:py-32 bg-brand-coal border-y border-white/[0.06]">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Eyebrow>Per ogni evento</Eyebrow>
            <MaskLines
              lines={["PER OGNI EVENTO,", "IL SUO SPRIZZIAMOCI."]}
              when="view"
              lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.4rem,5.5vw,4.8rem)]"
              className="mt-6"
            />
          </div>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-white/50 md:text-base">
              I grandi eventi sono il nostro terreno di casa; matrimoni, feste private ed eventi
              aziendali sono le occasioni in cui lo stesso servizio diventa riservato e dedicato.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <EventCard e={EVENTS[0]} className="md:col-span-7 h-[420px] md:h-[540px]" />
          <EventCard e={EVENTS[1]} className="md:col-span-5 h-[420px] md:h-[540px]" />
          <EventCard e={EVENTS[2]} className="md:col-span-5 h-[420px] md:h-[540px]" />
          <EventCard e={EVENTS[3]} className="md:col-span-7 h-[420px] md:h-[540px]" />
        </div>
      </div>
    </section>
  );
}

function SetupTeaser() {
  const items = [
    { img: IMG.banco, t: "Banco di mescita", d: "Erogatori per spritz, campari, gin tonic e gin lemon." },
    { img: IMG.limeIce, t: "Banco di preparazione", d: "Ghiaccio, arance, accessori e area servizio." },
    { img: IMG.glassesIce, t: "Attrezzature e materiali", d: "Spillatura, fusti, ghiaccio, bicchieri: secondo il pacchetto." },
  ];
  return (
    <section className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Eyebrow>Il nostro allestimento</Eyebrow>
        <MaskLines
          lines={["ARRIVIAMO NOI.", "PREPARIAMO TUTTO.", "SI BRINDA."]}
          when="view"
          lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.4rem,5.5vw,4.8rem)]"
          className="mt-6"
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {items.map((it, i) => (
            <Reveal key={it.t} delay={i * 0.08}>
              <div className="group rounded-3xl border border-white/[0.07] bg-brand-surface/30 overflow-hidden h-full transition-colors duration-300 hover:border-brand-orange/40">
                <div className="h-52 overflow-hidden">
                  <img
                    src={it.img}
                    alt={it.t}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl font-semibold">{it.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">{it.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15} className="mt-10">
          <CTAButton to="/allestimenti" variant="outline" testId="setup-teaser-cta">
            Tutti gli allestimenti
          </CTAButton>
        </Reveal>
      </div>
    </section>
  );
}

function CocktailsRow() {
  return (
    <section className="py-24 md:py-32 bg-brand-coal border-y border-white/[0.06]">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Eyebrow>I nostri cocktail</Eyebrow>
            <MaskLines
              lines={["SCEGLI IL TUO", "PROSSIMO BRINDISI."]}
              when="view"
              lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.4rem,5.5vw,4.8rem)]"
              className="mt-6"
            />
          </div>
          <Reveal>
            <CTAButton to="/cocktail-e-servizi" variant="outline" testId="cocktail-teaser-cta">
              Cocktail e servizi
            </CTAButton>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {COCKTAILS.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.06}>
              <div className="group relative overflow-hidden rounded-2xl border border-white/[0.06] aspect-[3/4]">
                <img
                  src={c.image}
                  alt={c.alt}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.07]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="font-display text-xl md:text-2xl font-semibold">{c.name}</p>
                  {c.note && <p className="mt-1 text-[11px] text-white/60">{c.note}</p>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 text-sm text-white/55">
          E anche: <span className="text-brand-cream font-medium">birra, vino e prosecco, analcolici</span> — sempre alla spina.
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Eyebrow>Come funziona</Eyebrow>
        <MaskLines
          lines={["TU SCEGLI L'OCCASIONE.", "NOI COSTRUIAMO IL SERVIZIO."]}
          when="view"
          lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.4rem,5.5vw,4.8rem)]"
          className="mt-6"
        />
        <div className="mt-16 grid gap-10 md:grid-cols-4 md:gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="relative border-t-2 border-brand-orange/30 pt-6">
                <span className="font-display italic text-brand-orange text-5xl leading-none">{s.n}</span>
                <h3 className="mt-4 font-display text-xl md:text-2xl font-semibold uppercase tracking-wide leading-snug">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PartnerBand() {
  const ruoli = [
    { who: "SPRIZZIAMOCI", what: "Banco con erogatori alla spina, prodotto, personale di servizio, ghiaccio e bicchieri. Al resto ci pensiamo noi, dal montaggio allo smontaggio." },
    { who: "L'ORGANIZZATORE", what: "Solo lo spazio a disposizione per il banco, dentro la tua manifestazione o il tuo evento: al resto pensiamo noi." },
  ];
  return (
    <section className="py-24 md:py-32 bg-brand-coal border-y border-white/[0.06]">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="lg:col-span-8">
          <Eyebrow>Partnership per organizzatori</Eyebrow>
          <MaskLines
            lines={["ORGANIZZI EVENTI?", "DIVENTA PARTNER SOCIO."]}
            when="view"
            lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.4rem,5vw,4.6rem)]"
            className="mt-6"
          />
          <Reveal delay={0.15}>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-white/65 max-w-2xl">
              Manifestazioni, festival, feste di piazza: noi mettiamo tutto — attrezzature,
              prodotto e personale di servizio — tu metti solo lo spazio. Il tuo pubblico
              brinda, tu guadagni su ogni drink. Un sistema operativo nato per i grandi
              numeri: zero investimenti in prodotto e risorse umane per le organizzazioni.
            </p>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {ruoli.map((r, i) => (
            <Reveal key={r.who} delay={i * 0.08}>
              <div className="rounded-3xl border border-white/[0.07] bg-brand-surface/30 p-8 h-full transition-colors duration-300 hover:border-brand-orange/40">
                <p className="eyebrow">{r.who}</p>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-white/70">{r.what}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.16}>
            <div className="rounded-3xl border border-brand-orange/40 bg-brand-orange/[0.06] p-8 h-full">
              <p className="eyebrow">Il tuo guadagno</p>
              <p className="mt-4 font-display text-4xl md:text-5xl font-semibold text-brand-orange">1,50 €</p>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                per ogni drink venduto: come partner socio incassi direttamente al tuo evento.
              </p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <CTAButton to="/partner" testId="partner-band-cta">
              Diventa partner socio
            </CTAButton>
            <CTAButton to="/preventivo" variant="outline" testId="partner-band-cta-preventivo">
              Preventivo eventi privati
            </CTAButton>
          </div>
          <p className="mt-6 text-[11px] leading-relaxed text-white/40 max-w-2xl">
            Eventi privati: preventivo dedicato. Grandi eventi pubblici: partnership socio con
            guadagno sul venduto. Ogni collaborazione viene valutata a discrezione
            dell'organizzazione Sprizziamoci.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function GalleryTeaser() {
  const imgs = [
    { src: IMG.weddingGarden, r: "aspect-[4/5]" },
    { src: IMG.friendsToast, r: "aspect-[4/5]" },
    { src: IMG.corpNet, r: "aspect-[4/5]" },
    { src: IMG.publicLanterns, r: "aspect-[4/5]" },
  ];
  return (
    <section className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Eyebrow>Gallery</Eyebrow>
            <MaskLines
              lines={["UN BANCO.", "MILLE OCCASIONI", "PER BRINDARE."]}
              when="view"
              lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.4rem,5vw,4.4rem)]"
              className="mt-6"
            />
          </div>
          <Reveal>
            <CTAButton to="/gallery" variant="outline" testId="gallery-teaser-cta">
              Esplora la gallery
            </CTAButton>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {imgs.map((im, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className={`overflow-hidden rounded-2xl ${im.r} group`}>
                <img
                  src={im.src}
                  alt="Anteprima gallery Sprizziamoci"
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GrandiNumeriBand() {
  return (
    <section data-testid="home-grandi-numeri" className="bg-brand-orange text-brand-ink overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24 grid gap-12 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-7">
          <p className="text-[11px] font-medium uppercase tracking-[0.35em] opacity-80 md:text-xs">
            I grandi eventi: la nostra casa
          </p>
          <MaskLines
            lines={["UN BAR FATTO", "PER I GRANDI NUMERI."]}
            when="view"
            lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2.4rem,5.5vw,4.8rem)]"
            className="mt-5"
          />
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-xl text-base font-medium leading-relaxed md:text-lg">
              Una potenza di servizio enorme: banchi in rete, erogazione alla spina e self
              service. Il servizio di attesa è quasi nullo, il pubblico brinda senza code e la
              manifestazione non si ferma mai.
            </p>
          </Reveal>
          <div className="mt-8 grid max-w-md grid-cols-2 gap-5">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-brand-ink/20 bg-brand-ink/[0.06] p-6 text-center">
                <p className="font-display text-4xl font-semibold md:text-5xl">fino a 50</p>
                <p className="mt-2 text-xs font-medium md:text-sm">cocktail al minuto*</p>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="rounded-3xl border border-brand-ink/20 bg-brand-ink/[0.06] p-6 text-center">
                <p className="font-display text-4xl font-semibold md:text-5xl">fino a 3.000</p>
                <p className="mt-2 text-xs font-medium md:text-sm">drink all'ora*</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/eventi-pubblici"
              data-testid="home-grandi-numeri-cta"
              className="mt-9 inline-flex items-center gap-3 rounded-full bg-brand-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-black"
            >
              Ospita Sprizziamoci ai grandi eventi <ArrowUpRight size={15} />
            </Link>
          </Reveal>
          <Reveal>
            <p className="mt-6 max-w-2xl text-[11px] leading-relaxed opacity-75">
              *Capacità teorica dichiarata, da verificare con il fornitore e mediante test
              operativi: non costituisce una prestazione garantita o certificata.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal>
            <img
              src={IMG.bancoEvento}
              alt="Il banco Sprizziamoci con tenda arancione ed erogatori alla spina ai grandi eventi"
              className="aspect-[16/10] w-full rounded-3xl object-cover"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useSEO(
    "Sprizziamoci — L'aperitivo on the road | Cocktail e bevande alla spina per eventi",
    "Sprizziamoci porta l'aperitivo al tuo evento: banco beverage professionale in self service, cocktail e bevande alla spina per matrimoni, feste private, eventi aziendali e manifestazioni."
  );
  return (
    <>
      <Hero />
      <Marquee />
      <FormatInnovativo />
      <GrandiNumeriBand />
      <EventsBento />
      <SetupTeaser />
      <CocktailsRow />
      <HowItWorks />
      <PartnerBand />
      <GalleryTeaser />
    </>
  );
}
